import React, { useEffect, useState } from "react";
import { auth, db } from "../../firebase/config";
import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";

const ReactivateAccount = () => {
  const navigate = useNavigate();
  const user = auth.currentUser;

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [reactivating, setReactivating] = useState(false);

  useEffect(() => {
    const loadAccount = async () => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }

      try {
        const userSnap = await getDoc(doc(db, "users", user.uid));
        const userData = userSnap.exists() ? userSnap.data() : null;

        setProfile(userData);

        if (userData?.accountStatus !== "deactivated") {
          navigate("/map");
        }
      } catch (error) {
        console.error("Error loading deactivated account:", error);
        alert("Could not load account details.");
      } finally {
        setLoading(false);
      }
    };

    loadAccount();
  }, [user?.uid, navigate]);

  const handleReactivate = async () => {
    if (!user?.uid) {
      alert("Please sign in again.");
      return;
    }

    if (
      !window.confirm(
        "Reactivate your account? Founder and premium access will remain inactive unless restored by an admin."
      )
    ) {
      return;
    }

    try {
      setReactivating(true);

      const userRef = doc(db, "users", user.uid);
      const backup = profile?.deactivatedProfileBackup || {};

      await updateDoc(userRef, {
        accountStatus: "active",
        isPublic: true,
        name: backup.name || "User",
        bio: backup.bio || "",
        intro: backup.intro || "",
        phone: backup.phone || "",
        profilePicture: backup.profilePicture || "",
        founderSpotActive: false,
        premiumAccess: false,
        subscriptionStatus: "required",
        subscriptionRequired: true,
        plan: "free",
        reactivatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      alert("Welcome back! Your account has been reactivated.");

      navigate("/map");
    } catch (error) {
      console.error("Error reactivating account:", error);
      alert("Could not reactivate account: " + error.message);
    } finally {
      setReactivating(false);
    }
  };

  const handleStayDeactivated = async () => {
    await signOut(auth);
    navigate("/");
  };

  if (loading) {
    return (
      <div className="card page-shell page-shell-narrow">
        <h2>Checking account...</h2>
        <p>One second while we check your account status.</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="card page-shell page-shell-narrow">
        <h2>Please sign in</h2>
        <p>You’ll need to sign in before reactivating your account.</p>
        <button type="button" className="btn-primary" onClick={() => navigate("/")}>
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <div className="card page-shell page-shell-narrow reactivate-card">
      <div className="reactivate-hero">
        <div className="reactivate-icon">🌱</div>

        <div>
          <h2>Reactivate Your Account</h2>
          <p>
            This account is currently deactivated. You can bring it back and
            continue using MyFarmingPal.
          </p>
        </div>
      </div>

      <div className="reactivate-status-box">
        <strong>Standard account</strong>
        <p>You can reactivate your account and continue using MyFarmingPal.</p>
      </div>

      <div className="reactivate-actions">
        <button
          type="button"
          className="btn-primary"
          disabled={reactivating}
          onClick={handleReactivate}
        >
          {reactivating ? "Reactivating..." : "Reactivate Account"}
        </button>

        <button
          type="button"
          className="btn-danger-soft"
          disabled={reactivating}
          onClick={handleStayDeactivated}
        >
          Stay Deactivated
        </button>
      </div>
    </div>
  );
};

export default ReactivateAccount;
